import { useToast } from '@app/core/notifications';
import { PreferenceSet } from '@knocklabs/client';
import { useKnockClient, usePreferences } from '@knocklabs/react';
import { CircularProgress, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { notificationWorkflows } from '../workflows';
import { NotificationPreferencesForm } from './NotificationPreferencesForm';
import {
  createDefaultNotificationPreferences,
  readWorkflowChannelPrefs,
  toKnockWorkflowPreferences,
} from './notification-preferences-defaults';
import { notificationPreferencesSaveErrorMessage } from './notification-preferences-messages';
import { NotificationPreferencesValues } from './notification-preferences-types';

/**
 * Maps a Knock PreferenceSet into the form values shape.
 */
function preferenceSetToValues(
  preferences: PreferenceSet | undefined,
): NotificationPreferencesValues {
  const defaults = createDefaultNotificationPreferences();
  if (!preferences?.workflows) {
    return defaults;
  }

  for (const workflow of notificationWorkflows) {
    defaults[workflow.key] = readWorkflowChannelPrefs(
      preferences.workflows[workflow.key],
      workflow,
      defaults[workflow.key],
    );
  }
  return defaults;
}

/**
 * Loads and saves notification preferences via the Knock preferences API.
 */
export function NotificationPreferencesEditor() {
  const knock = useKnockClient();
  const { preferences, isLoading } = usePreferences();
  const { notifyError } = useToast();
  const { formatMessage } = useIntl();
  const [values, setValues] = useState<NotificationPreferencesValues>(() =>
    createDefaultNotificationPreferences(),
  );
  const [saving, setSaving] = useState(false);

  const remoteValues = useMemo(() => preferenceSetToValues(preferences), [preferences]);

  useEffect(() => {
    setValues(remoteValues);
  }, [remoteValues]);

  const handleChange = useCallback(
    async (next: NotificationPreferencesValues) => {
      setValues(next);
      setSaving(true);
      try {
        await knock.user.setPreferences({
          workflows: toKnockWorkflowPreferences(next),
        });
      } catch {
        setValues(remoteValues);
        notifyError(formatMessage(notificationPreferencesSaveErrorMessage));
      } finally {
        setSaving(false);
      }
    },
    [formatMessage, knock.user, notifyError, remoteValues],
  );

  if (isLoading && !preferences) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return <NotificationPreferencesForm values={values} disabled={saving} onChange={handleChange} />;
}
